import React, { memo, useContext } from 'react'
import moment from 'moment-timezone'
import { Avatar } from 'transfix-ui/components/Avatar'
import { TableRow, TableCell } from 'transfix-ui/components/Table'
import { Box } from 'transfix-ui/core'
import { $enum } from 'ts-enum-util'
import { IPricingTypes } from 'types/graphqlTypes'
import { FeatureFlagsContext } from 'utils/context/FeatureFlagsContext'
import { ShipperRateObject } from 'apps/marketplace/apiTypes/shipperRates'
import {
  formatMiles,
  formatPostalCode,
  formatPricingProgram,
} from 'apps/marketplace/utils/helpers/formatShipperLaneRow'
import {
  EQUIPMENT_TYPE,
  RATE_TYPE_DISPLAY,
  SHIPPER_RATES_COLUMNS,
} from 'apps/rms/types/shipperRates'
import { TrueRatePlusFilledIcon } from 'transfix-icons'
import { capitalize } from 'utils/capitalize'
import { FORMATS } from 'constants/datetime'
import { DISPLAY } from 'constants/display'

interface IShipperRateHistoryTableRowProps {
  shipperRate: ShipperRateObject
}

export const ShipperRateHistoryTableRow = memo<IShipperRateHistoryTableRowProps>(
  ({ shipperRate }) => {
    const { getFlag } = useContext(FeatureFlagsContext)

    const { enabled: mpRmsRepricedRates } = getFlag('mpRmsRepricedRates')

    const {
      awardType,
      delivery,
      endDate,
      equipment,
      id,
      isDeliveryDropTrailer,
      isPickupDropTrailer,
      isRepricedRate,
      minChargeRate,
      pickup,
      pricingProgram,
      pricingType,
      rate,
      shipperApplication,
      shipperLaneId,
      shipperMiles,
      startDate,
      volume,
      volumeType,
    } = shipperRate || {}

    const { amount: rateAmount, rateTypeKey: rateType } = rate || {}
    const { name: shipperName, opsRep } = shipperApplication || {}
    const { firstName = '', lastName = '' } = opsRep || {}
    const { dateFormatShort } = FORMATS
    const program = pricingProgram ? pricingProgram.program : undefined
    const pickupDrop = isPickupDropTrailer ? 'Drop' : 'Live'
    const deliveryDrop = isDeliveryDropTrailer ? 'Drop' : 'Live'
    const { key: equipmentKey } = equipment || {}
    const {
      city: pickupCity,
      postalCode: pickupPostalCode = '',
      postalCode3: pickupPostalCode3 = '',
      stateCode: pickupStateCode,
    } = pickup || {}
    const {
      city: deliveryCity,
      postalCode: deliveryPostalCode = '',
      postalCode3: deliveryPostalCode3 = '',
      stateCode: deliveryStateCode,
    } = delivery || {}

    const tableCells = [
      { label: 'id', value: id || DISPLAY.empty },
      { label: 'shipperLaneId', value: shipperLaneId || DISPLAY.empty },
      {
        label: SHIPPER_RATES_COLUMNS.isRepricedRate,
        value: isRepricedRate ? 'Yes' : DISPLAY.empty,
      },
      { label: 'shipper', value: shipperName || DISPLAY.empty },
      {
        label: 'equipment',
        value: $enum(EQUIPMENT_TYPE).getValueOrDefault(equipmentKey, DISPLAY.empty),
      },
      {
        label: 'startDate',
        value: startDate ? moment(startDate).format(dateFormatShort) : DISPLAY.empty,
      },
      {
        label: 'endDate',
        value: endDate ? moment(endDate).format(dateFormatShort) : DISPLAY.empty,
      },
      { label: 'awardType', value: awardType ? capitalize(awardType) : DISPLAY.empty },
      {
        label: 'pickupCity',
        value: pickupCity ? `${capitalize(pickupCity)}, ${pickupStateCode}` : DISPLAY.empty,
      },
      {
        label: 'pickupZipcode',
        value: formatPostalCode(pickupPostalCode || undefined, pickupPostalCode3 || undefined),
      },
      {
        label: 'deliveryCity',
        value: deliveryCity ? `${capitalize(deliveryCity)}, ${deliveryStateCode}` : DISPLAY.empty,
      },
      {
        label: 'deliveryZipcode',
        value: formatPostalCode(deliveryPostalCode || undefined, deliveryPostalCode3 || undefined),
      },
      { label: 'shipperMiles', value: formatMiles(Number(shipperMiles)) },
      {
        label: 'pricingProgram',
        pricingType,
        value: formatPricingProgram(pricingType, program),
      },
      {
        label: 'rate',
        rateType: $enum(RATE_TYPE_DISPLAY).getValueOrDefault(rateType, undefined),
        value: rateAmount || DISPLAY.empty,
      },
      { label: 'minCharge', value: minChargeRate?.amount || DISPLAY.empty },
      { label: 'volume', value: volume || DISPLAY.empty },
      { label: 'volumeType', value: volumeType ? capitalize(volumeType) : DISPLAY.empty },
      { label: 'dropType', value: `${pickupDrop} - ${deliveryDrop}` },
    ]

    return (
      <TableRow dataTest={`shipper-rate-${id}`} highlightOnHover>
        {tableCells
          .filter(cell => cell.label !== SHIPPER_RATES_COLUMNS.isRepricedRate || mpRmsRepricedRates)
          .map(cell => (
            <TableCell dataTest={`shipper-rate-${id}-${cell.label}`} key={`{id}-${cell.label}`}>
              {cell.value}
              {cell.label === 'rate' && cell.rateType && (
                <p className='text-small'>{cell.rateType}</p>
              )}
              {cell.label === 'pricingProgram' && cell.pricingType === IPricingTypes.variable && (
                <Box display='flex' flexDirection='row' alignItems='center'>
                  <TrueRatePlusFilledIcon size='large' color='coolGray8' />
                </Box>
              )}
            </TableCell>
          ))}
        <TableCell colSpan={2} dataTest={`shipper-rate-history-table-${id}-accountManager`}>
          {firstName && <Avatar name={`${firstName} ${lastName}`} />}
        </TableCell>
      </TableRow>
    )
  }
)
